import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";

import { API_BASE_URL } from "../config";
import useIsMountedRef from "../utils/mountedRef";
import BadgeNouveau from "../components/texts/BadgeNouveau";
import LoyiaInlineIcon from "./loyia/LoyiaInlineIcon";
import apiIntercepteur from "../apiIntercepteur";
import PenLoader from "../components/loaders/PenLoader";
import { ToolTipPerso } from "../components/selects/tooltip_perso";
import FormalitesNoticeModal from "../components/PopUp/FormalitesNoticeModal";

import "./Home.css";

export const OTHERS = [42, 103, 104, 105, 106, 113, 116, 118];
export const DMTG = [55, 56, 109, 110];
export const DEACTIVATED = [86, 87, 88, 93, 94, 107, 108];
//pour les nouveaux simulateurs que l'on souhaite mettre en avant
export const NEWSIMIDS = [52, 55, 76, 84, 85, 92, 113, 114, 115, 117, 118];
export const NEWSUBCATEGORIES = [6, 13, 14, 16, 17, 28];

const shouldShowHomeSubcategory = (categoryUrlName, subcategory) =>
  categoryUrlName === "patrimoine" || Number(subcategory.id) !== 7;

const hasVisibleHomeSimulateurs = (subcategory) =>
  !Array.isArray(subcategory.simulateur) ||
  subcategory.simulateur.some((simulateur) => !DEACTIVATED.includes(Number(simulateur.id)));

const readHomeCache = (key, fallbackValue) => {
  try {
    const cachedValue = sessionStorage.getItem(key);
    return cachedValue ? JSON.parse(cachedValue) : fallbackValue;
  } catch (error) {
    return fallbackValue;
  }
};

function Home() {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const isMountedRef = useIsMountedRef();

  const [categories, setCategories] = useState(() => readHomeCache("home.categories", []));
  const [simulateurSubcategories, setSimulateurSubcategories] = useState(() =>
    readHomeCache("home.simulateurSubcategories", {})
  );
  const [categoriesOther, setCategoriesOther] = useState(() => readHomeCache("home.categoriesOther", []));
  const [openMenuKey, setOpenMenuKey] = useState(null);

  
  const expectedSimulateurCategoryCount = categories.filter((category) => category.url_name !== "autres").length;
  const isSimulateursReady =
    categories.length > 0 &&
    (expectedSimulateurCategoryCount === 0 ||
      Object.keys(simulateurSubcategories).length >= expectedSimulateurCategoryCount);
  const isCalculateursReady = categoriesOther.length > 0;
  const isLoading = !(isSimulateursReady && isCalculateursReady);

  const handleUnauthorized = (error) => {
    if (error.response?.status === 401 && error.response.data != null) {
      if (error.response.data.error === "Other_user_connected") {
        localStorage.removeItem("user");
        localStorage.setItem("other_user_connected", "other_user_connected");
        window.location.href = "/login";
      }

      if (error.response.data.error === "Never connected" || error.response.data.error === "Unauthorized") {
        localStorage.removeItem("user");
        window.location.href = "/login";
      }
    }
  };

  const navigateTo = (path) => {
    setOpenMenuKey(null);
    history.push(path);
  };

  const toggleDropdown = (key) => {
    setOpenMenuKey((currentKey) => (currentKey === key ? null : key));
  };
  const isDesktopHomeHoverNavigation = () =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 1101px)").matches;

  const openHomeDropdownOnHover = (key) => {
    if (!isDesktopHomeHoverNavigation()) {
      return;
    }

    setOpenMenuKey(key);
  };

  const closeHomeDropdownOnHover = (key) => {
    if (!isDesktopHomeHoverNavigation()) {
      return;
    }

    setOpenMenuKey((currentKey) => (currentKey === key ? null : currentKey));
  };

  const getSimulateurPath = (simulateur) => {
    if (!simulateur?.id) {
      return "/";
    }

    if (DMTG.includes(simulateur.id)) {
      return `/categorie/autres/dmtg/${simulateur.id}`;
    }

    if (OTHERS.includes(simulateur.id)) {
      const categoryUrl = simulateur.id >= 113 ? "patrimoine" : "autres";
      return `/categorie/${categoryUrl}/others/${simulateur.id}`;
    }

    const categoryUrl = simulateur.subcategorie?.[0]?.categorie?.url_name || "categorie";
    return `/categorie/${categoryUrl}/simulateur/${simulateur.id}`;
  };

  const simulateursDeFrais = categories
    .filter((category) => category.url_name !== "autres")
    .map((category) => {
      const subcategories = (simulateurSubcategories[category.url_name] || []).filter(
        (subcategory) => shouldShowHomeSubcategory(category.url_name, subcategory) && hasVisibleHomeSimulateurs(subcategory)
      );

      return {
        key: category.url_name,
        label: category.categorie_name,
        badge: ["famille", "patrimoine"].includes(category.url_name),
        onClick: () => navigateTo(`/categorie/${category.url_name}`),
        submenu:
          category.url_name === "patrimoine"
            ? undefined
            : subcategories
              .map((subcategory) => {
                const subcategoryLabel = subcategory.shortname || subcategory.name;

                return {
                  label: subcategoryLabel,
                  badge: NEWSUBCATEGORIES.includes(Number(subcategory.id)),
                  onClick: () =>
                    navigateTo(
                      `/categorie/${category.url_name}?subcategoryId=${subcategory.id}&subcategory=${encodeURIComponent(
                        subcategoryLabel
                      )}`
                    ),
                };
              }),
      };
    })
    .filter((category) => category.key === "patrimoine" || category.submenu?.length > 0);

  const boiteAOutils = [
    {
      key: "tarif-notaires",
      label: "Le tarif des notaires et la fiscalité des actes",
      emphasis: true,
      onClick: () => navigateTo("/tarif_des_notaires"),
      submenu: [
        { label: "Émoluments d'acte", onClick: () => navigateTo("/tarif_des_notaires#emolActes") },
        { label: "Émoluments de formalités", onClick: () => navigateTo("/tarif_des_notaires#emolForm") },
        { label: "Fiscalité", onClick: () => navigateTo("/tarif_des_notaires#fisca") },
        { label: "Droits fixes", onClick: () => navigateTo("/tarif_des_notaires#droitsFixes") },
        { label: "Débours", onClick: () => navigateTo("/tarif_des_notaires#debours") },
      ],
    },
    {
      key: "fiches-pratiques",
      label: "Les fiches pratiques",
      emphasis: true,
      badge: true,
      onClick: () => navigateTo("/fiches"),
      submenu: [
        { label: "Immobilier", onClick: () => navigateTo("/fiches#fiches-immobilier") },
        { label: "Famille", onClick: () => navigateTo("/fiches#fiches-famille") },
        { label: "Sociétés", onClick: () => navigateTo("/fiches#fiches-societes") },
        { label: "Divers", onClick: () => navigateTo("/fiches#fiches-divers") },
      ],
    },
    {
      key: "actes-affaires-outils",
      label: "Actes d'Affaires : formalités, démarches préalables et droits d'enregistrements",
      emphasis: true,
      disabled: true,
      badgeText: "À VENIR",
      badgeInline: true,
    },
  ];

  const expertiseEtPratique = [
    {
      key: "coin-experts",
      label: "Le Coin des experts",
      emphasis: true,
      onClick: () => navigateTo("/avis"),
      submenu: [
        { label: "Famille", onClick: () => navigateTo("/avis?filter=famille") },
        { label: "Immobilier", onClick: () => navigateTo("/avis?filter=immobilier") },
        { label: "Divers", onClick: () => navigateTo("/avis?filter=divers") },
      ],
    },
    {
      key: "pratique",
      label: "La Méthode Taxe et Actes® issue des Pratiques I et II",
      title: "Méthode pour identifier tous les éléments d'un acte qui impactent le calcul de la taxe et ne rien oublier",
      emphasis: true,
      disabled: true,
      badgeText: "À VENIR",
      badgeInline: true,
      // submenu: [
      //   { label: "Actes courants", onClick: () => navigateTo("/pratique?section=actes-courants") },
      //   { label: "Actes famille", onClick: () => navigateTo("/pratique?section=actes-famille") },
      // ],
      // dropdownAlign: "right",
    },
    {
      key: "loyia",
      label: "LOYIA, l'Intelligence au service de votre métier : posez-moi une question !",
      emphasis: true,
      onClick: () => navigateTo("/loyia"),
    },
  ];

  const specificSubcategoryId = 27; 
  const specificCategory = categoriesOther.find(category => category.id === specificSubcategoryId);
  
  const specificSimulators = specificCategory ? 
    specificCategory.simulateur.filter(simulator => !DEACTIVATED.includes(simulator.id)) : [];

  const groupedByCategory = specificCategory ? {
    [specificCategory.shortname || specificCategory.name]: specificSimulators
  } : {};

  const calculateursDivers = [
    // Autres simulateurs (non spécifiques)
    ...(categoriesOther[0]?.simulateur?.filter((item) => 
      !DEACTIVATED.includes(item.id) && 
      !specificSimulators.some(specific => specific.id === item.id)
    ).map((item) => ({
      key: `autres-${item.id}`,
      label: item.name,
      badge: NEWSIMIDS.includes(item.id),
      onClick: () => navigateTo(getSimulateurPath(item)),
    })) || []),
    ...(categoriesOther[2]?.simulateur?.filter((item) => 
      !DEACTIVATED.includes(item.id) && 
      !specificSimulators.some(specific => specific.id === item.id)
    ).map((item) => ({
      key: `patrimoine-${item.id}`,
      label: item.name,
      badge: NEWSIMIDS.includes(item.id),
      onClick: () => navigateTo(getSimulateurPath(item)),
    })) || []),
    ...Object.entries(groupedByCategory).map(([categoryName, simulators]) => ({
      key: `category-${categoryName.replace(/\s+/g, '-').toLowerCase()}`,
      label: categoryName,
      onClick: () => navigateTo(`/categorie/patrimoine?subcategoryId=${specificCategory.id}&subcategory=${encodeURIComponent(specificCategory.shortname || specificCategory.name)}`),
      submenu: simulators.map((simulator) => ({
        label: simulator.shortname || simulator.name,
        badge: NEWSIMIDS.includes(simulator.id),
        onClick: () => navigateTo(getSimulateurPath(simulator)),
      })),
    })),
  ];

  useEffect(() => {
    const categoriesRequest = {
      method: "get",
      url: `${API_BASE_URL}/categories`,
      headers: {
        "Content-Type": "application/json",
        Authorization: JSON.parse(localStorage.getItem("user"))?.jwt,
      },
    };

    apiIntercepteur(categoriesRequest)
      .then((response) => {
        if (response.status === 200 && response.data != null) {
          if (isMountedRef.current) {
            setCategories(response.data);
          }
        } else {
          enqueueSnackbar("Une erreur est survenue", { variant: "error" });
        }
      })
      .catch(handleUnauthorized);
  }, [enqueueSnackbar, isMountedRef]);

  useEffect(() => {
    if (categories.length > 0) {
      sessionStorage.setItem("home.categories", JSON.stringify(categories));
    }
  }, [categories]);

  useEffect(() => {
    if (!categories.length) {
      return undefined;
    }

    const loadSimulateurSubcategories = async () => {
      try {
        const categoryResponses = await Promise.all(
          categories
            .filter((category) => category.url_name !== "autres")
            .map((category) =>
              apiIntercepteur({
                method: "get",
                url: `${API_BASE_URL}/categories/${category.url_name}`,
                headers: {
                  "Content-Type": "application/json",
                  Authorization: JSON.parse(localStorage.getItem("user"))?.jwt,
                },
              }).then((response) => ({
                urlName: category.url_name,
                subcategories: response.data?.subcategories || [],
              }))
            )
        );

        if (isMountedRef.current) {
          setSimulateurSubcategories(
            categoryResponses.reduce((accumulator, categoryResponse) => {
              accumulator[categoryResponse.urlName] = categoryResponse.subcategories;
              return accumulator;
            }, {})
          );
        }
      } catch (error) {
        handleUnauthorized(error);
      }
    };

    loadSimulateurSubcategories();

    return undefined;
  }, [categories, isMountedRef]);

  useEffect(() => {
    const loadCalculateurSources = async () => {
      try {
        const [othersResponse, patrimoineResponse] = await Promise.all([
          apiIntercepteur({
            method: "get",
            url: `${API_BASE_URL}/categories/autres`,
            headers: {
              "Content-Type": "application/json",
              Authorization: JSON.parse(localStorage.getItem("user"))?.jwt,
            },
          }),
          apiIntercepteur({
            method: "get",
            url: `${API_BASE_URL}/categories/patrimoine`,
            headers: {
              "Content-Type": "application/json",
              Authorization: JSON.parse(localStorage.getItem("user"))?.jwt,
            },
          }),
        ]);

        if (!isMountedRef.current) {
          return;
        }

        setCategoriesOther([
          ...(othersResponse.data?.subcategories || []),
          ...(patrimoineResponse.data?.subcategories || []),
        ]);
      } catch (error) {
        handleUnauthorized(error);
      }
    };

    loadCalculateurSources();

    return undefined;
  }, [enqueueSnackbar, isMountedRef]);

  useEffect(() => {
    if (Object.keys(simulateurSubcategories).length > 0) {
      sessionStorage.setItem("home.simulateurSubcategories", JSON.stringify(simulateurSubcategories));
    }
  }, [simulateurSubcategories]);

  useEffect(() => {
    if (categoriesOther.length > 0) {
      sessionStorage.setItem("home.categoriesOther", JSON.stringify(categoriesOther));
    }
  }, [categoriesOther]);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!event.target.closest(".home-list-item--dropdown")) {
        setOpenMenuKey(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpenMenuKey(null);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const renderSubmenu = (items) => (
    <ul className="home-dropdown-list">
      {items.map((item) => (
        <li key={item.label} className="home-dropdown-item">
          <button type="button" className="home-dropdown-button" onClick={item.onClick}>
            {item.label}
            {item.badge && <BadgeNouveau cliquable={false} />}
          </button>
        </li>
      ))}
    </ul>
  );

  const renderLabelContent = (item) => (
    item.key === "loyia" ? (
      <span>
        <span className="home-loyia-inline-word">LOYIA</span>
        <LoyiaInlineIcon size={24} className="home-loyia-inline-icon" />
        <span className="home-loyia-inline-text">
          , l'Intelligence au service de votre métier : posez-moi une question !
        </span>
      </span>
    ) : item.key === "fiches-pratiques" && item.badge ? (
      <span>
        <span>{item.label}</span>
        <BadgeNouveau text={item.badgeText || "NOUVEAU"} cliquable={false} />
      </span>
    ) : item.badgeInline && (item.badge || item.badgeText) ? (
      <span className="home-inline-label">
        {item.key === "pratique" || item.key === "actes-affaires-outils" ? (
          <span className="home-inline-label-text">
            <span>{item.label}</span>
            <span className="home-inline-label-trailing">
              {item.key === "pratique" ? <ToolTipPerso title={item.title} /> : null}
              <BadgeNouveau
                text={item.badgeText || "NOUVEAU"}
                className="home-coming-soon-badge"
                cliquable={false}
              />
            </span>
          </span>
        ) : (
          <span>{item.label}</span>
        )}
        {item.key !== "pratique" && item.key !== "actes-affaires-outils" ? (
          <span className="home-inline-badge-slot">
            <BadgeNouveau
              text={item.badgeText || "NOUVEAU"}
              className="home-coming-soon-badge"
              cliquable={false}
            />
          </span>
        ) : null}
      </span>
    ) : (
      <span>{item.label}</span>
    )
  );

  const renderList = (items, listKey) => (
    <ul className="home-main-list">
      {items.map((item) => {
        const itemKey = `${listKey}-${item.key}`;
        const hasSubmenu = Array.isArray(item.submenu) && item.submenu.length > 0;
        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        const hasExpandableContent = hasSubmenu || hasChildren;
        const opensFromExpander = hasSubmenu && ["tarif-notaires", "fiches-pratiques", "coin-experts"].includes(item.key);
        const isOpen = openMenuKey === itemKey;

        return (
          <li
            key={itemKey}
            className={`home-list-item ${hasExpandableContent ? "home-list-item--dropdown" : ""} ${isOpen ? "is-open" : ""}`}
            onMouseLeave={hasSubmenu ? () => closeHomeDropdownOnHover(itemKey) : undefined}
            onMouseDown={(e) => { if (hasExpandableContent) e.stopPropagation(); }}
          >
            <div className="home-list-row">
              <button
                type="button"
                className={`home-link-button ${item.emphasis ? "home-link-button--strong" : ""} ${
                  item.disabled ? "home-link-button--disabled" : ""
                }`}
                disabled={Boolean(item.disabled)}
                aria-disabled={Boolean(item.disabled)}
                onClick={item.onClick}
              >
                {renderLabelContent(item)}
              </button>

              {hasExpandableContent ? (
                <>
                  <span className="home-expander-slot">
                    <button
                      type="button"
                      className="home-item-expander"
                      aria-expanded={isOpen}
                      aria-label={`Ouvrir ${item.label}`}
                      onMouseEnter={hasSubmenu ? () => openHomeDropdownOnHover(itemKey) : undefined}
                      onFocus={() => openHomeDropdownOnHover(itemKey)}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleDropdown(itemKey);
                      }}
                    >
                      ▾
                    </button>

                    {opensFromExpander ? (
                      <div
                        className={`home-dropdown home-dropdown--expander-side ${
                          item.dropdownAlign === "right" ? "home-dropdown--right" : ""
                        } ${item.key === "immobilier" ? "home-dropdown--immobilier" : ""} home-dropdown--${item.key}`}
                      >
                        {renderSubmenu(item.submenu)}
                      </div>
                    ) : null}
                  </span>
                  {(item.badge || item.badgeText) && !item.badgeInline && item.key !== "fiches-pratiques" ? (
                    <BadgeNouveau
                      text={item.badgeText || "NOUVEAU"}
                      className={item.badgeText ? "home-coming-soon-badge" : undefined}
                      cliquable={false}
                    />
                  ) : null}
                </>
              ) : (item.badge || item.badgeText) && !item.badgeInline && item.key !== "fiches-pratiques" ? (
                <BadgeNouveau
                  text={item.badgeText || "NOUVEAU"}
                  className={item.badgeText ? "home-coming-soon-badge" : undefined}
                  cliquable={false}
                />
              ) : null}
            </div>

            {hasChildren && isOpen ? (
              <ul className="home-sub-list">
                {item.children.map((child) => (
                  <li key={child.label} className="home-sub-list-item">
                    <button type="button" className="home-link-button home-link-button--sub" onClick={child.onClick}>
                      {child.label}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            {hasSubmenu && !opensFromExpander ? (
              <div
                className={`home-dropdown ${item.dropdownAlign === "right" ? "home-dropdown--right" : ""} ${
                  item.key === "immobilier" ? "home-dropdown--immobilier" : ""
                } home-dropdown--${item.key}`}
              >
                {renderSubmenu(item.submenu)}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );

  return isLoading ? (
    <div className="home-page-loader home-page-loader--standalone">
      <PenLoader className="home-page-pen-loader" />
    </div>
  ) : (
    <div className="home-global-container">
      <FormalitesNoticeModal />
      <div className="containerHome">

        <div className="home-grid">
          <section className="home-card home-card--blue">
            <div className="home-card-header">
              <h2>Simulateurs de frais</h2>
              <p>Projeter - Estimer - Décider</p>
            </div>
            <div className="home-card-body">{renderList(simulateursDeFrais, "simulateurs")}</div>
          </section>

          <section className="home-card home-card--light-blue">
            <div className="home-card-header">
              <h2>Calculateurs divers</h2>
              <p>Calculer - Valider - Sécuriser</p>
            </div>
            <div className="home-card-body">{renderList(calculateursDivers, "calculateurs")}</div>
          </section>

          <section className="home-card home-card--teal">
            <div className="home-card-header">
              <h2>La boîte à outils</h2>
              <p>S'informer - Vérifier - Appliquer</p>
            </div>
            <div className="home-card-body">{renderList(boiteAOutils, "outils")}</div>
          </section>

          <section className="home-card home-card--gold">
            <div className="home-card-header">
              <h2>Expertise &amp; Pratique</h2>
              <p>Approfondir - Se Former - Décrypter</p>
            </div>
            <div className="home-card-body">{renderList(expertiseEtPratique, "expertise")}</div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default Home;
